import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { cn } from "@apex/shared";

export interface CategorySectionProps {
  /** Display name for the category. */
  title: string;
  /** Total items currently rendered inside the section. */
  itemCount: number;
  /** Items inside the section that are currently selected. */
  selectedCount: number;
  /**
   * Default open state on first mount. Subsequent mounts honour user toggles.
   * Default: true (all categories expand by default — issue #535 acceptance).
   */
  defaultOpen?: boolean;
  /**
   * When true, force the section open regardless of the user's last toggle
   * choice. Used during accessory search (#534) so categories with matching
   * results auto-expand. Clearing the search drops `forceOpen` and the
   * previous user state wins again.
   */
  forceOpen?: boolean;
  /**
   * Callback when the user clicks the "(N selected)" badge while the section
   * is collapsed. The CatalogItemList wires this to expand + scroll to first
   * selected item.
   */
  onSelectedBadgeClick?: () => void;
  children?: ReactNode;
  className?: string;
}

/**
 * CategorySection — a collapsible section with a title, item count and
 * (when collapsed) a selected-count badge. Click anywhere in the header to
 * toggle. Smooth height animation respects `prefers-reduced-motion`.
 *
 * Accessibility:
 *   - Header is a `<button>` with `aria-expanded` and `aria-controls`
 *   - Body has `role="region"` and `aria-labelledby` linking to the header
 */
export function CategorySection({
  title,
  itemCount,
  selectedCount,
  defaultOpen = true,
  forceOpen,
  onSelectedBadgeClick,
  children,
  className,
}: CategorySectionProps) {
  const [userOpen, setUserOpen] = useState(defaultOpen);
  // Effective open: forceOpen wins over user preference. We DON'T mutate
  // `userOpen` when `forceOpen` flips on, so once the search clears the user's
  // own collapse state is preserved (issue #535 spec).
  const open = forceOpen ? true : userOpen;

  const headerId = useId();
  const contentId = useId();

  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | "none">("none");
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Measure content height for the max-height transition.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (open) {
      // After paint, snapshot scrollHeight then drop to "none" so subsequent
      // content changes inside (e.g. selecting an item) don't get clipped.
      setMaxHeight(el.scrollHeight);
      const t = setTimeout(() => setMaxHeight("none"), reduceMotion ? 0 : 220);
      return () => clearTimeout(t);
    }
    // Closing — first lock to current height to enable the transition, then
    // collapse to 0 on the next frame.
    setMaxHeight(el.scrollHeight);
    const r = requestAnimationFrame(() => setMaxHeight(0));
    return () => cancelAnimationFrame(r);
  }, [open, reduceMotion, children]);

  const toggle = () => {
    if (forceOpen) {
      // While forced open, a click should "release" the force — but since
      // `forceOpen` is owned by the parent (search term), the user's
      // intent is to collapse. Flip the user pref so when search clears the
      // user-pref state takes over.
      setUserOpen(false);
      return;
    }
    setUserOpen((v) => !v);
  };

  const handleBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open) setUserOpen(true);
    onSelectedBadgeClick?.();
  };

  return (
    <div className={cn("apex-rounded apex-overflow-hidden", className)}>
      <button
        id={headerId}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={contentId}
        className={cn(
          "apex-w-full apex-flex apex-items-center apex-gap-2",
          "apex-text-left apex-py-1.5",
          "apex-text-sm apex-font-semibold apex-text-muted-foreground apex-uppercase apex-tracking-wider",
          "hover:apex-text-foreground apex-transition-colors",
          "focus-visible:apex-outline-none focus-visible:apex-ring-2 focus-visible:apex-ring-ring focus-visible:apex-ring-offset-2",
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={cn(
            "apex-shrink-0 apex-transition-transform",
            reduceMotion ? "" : "apex-duration-200",
            open ? "apex-rotate-90" : "apex-rotate-0",
          )}
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
        <span>{title}</span>
        <span className="apex-text-xs apex-font-normal apex-text-muted-foreground apex-normal-case apex-tracking-normal">
          ({itemCount})
        </span>
        {!open && selectedCount > 0 && (
          <span
            role="button"
            tabIndex={0}
            onClick={handleBadgeClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleBadgeClick(e as unknown as React.MouseEvent);
              }
            }}
            aria-label={`${selectedCount} selected — click to expand`}
            className={cn(
              "apex-ml-auto apex-px-2 apex-py-0.5 apex-rounded-full",
              "apex-text-[11px] apex-font-medium apex-normal-case apex-tracking-normal",
              "apex-bg-primary apex-text-primary-foreground",
              "hover:apex-opacity-90 apex-transition-opacity apex-cursor-pointer",
            )}
          >
            {selectedCount} selected
          </span>
        )}
      </button>

      <div
        id={contentId}
        role="region"
        aria-labelledby={headerId}
        ref={contentRef}
        style={{
          maxHeight: maxHeight === "none" ? undefined : `${maxHeight}px`,
          overflow: maxHeight === "none" ? undefined : "hidden",
          transition: reduceMotion ? undefined : "max-height 200ms ease-out",
        }}
        hidden={!open && maxHeight === 0}
      >
        {children}
      </div>
    </div>
  );
}
